// CinematicCamera v2 — species-aware auto-fit rig.
//
// KEY UPGRADE (iter31 spec):
//   "Auto-fit cinematic camera that always keeps both creatures fully visible."
//
// The camera continually computes the smallest sphere that contains BOTH
// creature bounding volumes (derived from their species spec size + a
// per-species framing padding) and positions itself at the exact focal
// distance where that sphere fills ~85% of the shorter viewport axis.
// This means a Wolf-vs-Fox battle frames tighter than a Polar-Bear-vs-Moose
// battle — automatically, no per-fight tuning.
//
// The rig then adds cinematic overlays on top of the auto-fit baseline:
//   - Per-event angle changes (OTS on crit, low-wide on combo, overhead on ultimate)
//   - Shake on impact
//   - Punch-zoom on crit / ultimate
//   - Slow-mo respects state.slowMoUntil
//
// The old fixed-position rig is replaced by this. It solves the biggest
// visible bug in iter30 (creatures cropped or off-screen).
import { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { resolveSpecies, computeCombatPositions } from '../../lib/species_registry';

// Relative offsets applied to the auto-fit baseline. Values are unit fractions
// of the sphere radius so they scale with the battle's size envelope.
const ANGLE_OFFSETS = {
  wide:     { x: 0.0, y: 1.6, z: 3.2 },  // straight-on documentary
  ots_ch:   { x: -1.4, y: 1.1, z: 2.4 },
  ots_op:   { x:  1.4, y: 1.1, z: 2.4 },
  low_wide: { x: 0.0, y: 0.5, z: 2.6 },
  overhead: { x: 0.0, y: 3.6, z: 1.4 },
  tracking: { x: 0.0, y: 1.2, z: 2.6 },
  finale:   { x: -0.6, y: 1.9, z: 2.9 },
};

export default function CinematicCamera({
  chSnap, opSnap, chPosition, opPosition,
  lastLineKind, cursor, finished, lowPower, slowMoUntil,
}) {
  const { camera, size } = useThree();

  // If the caller didn't pass explicit positions (legacy call sites) fall
  // back to the shared species-aware helper so scene + camera agree.
  const positions = useMemo(() => {
    if (chPosition && opPosition) return { chPos: chPosition, opPos: opPosition };
    return computeCombatPositions(chSnap, opSnap);
  }, [chPosition, opPosition, chSnap, opSnap]);
  const chPos = positions.chPos;
  const opPos = positions.opPos;

  // Compute framing envelope from BOTH species at once — larger animal wins.
  const frame = useMemo(() => {
    const specCh = resolveSpecies(chSnap);
    const specOp = resolveSpecies(opSnap);
    const pad = Math.max(specCh.camera?.framing_padding ?? 1.2, specOp.camera?.framing_padding ?? 1.2);
    // Sphere covering both creatures at their world positions.
    const chH = specCh.size.height_m, opH = specOp.size.height_m;
    const chL = specCh.size.length_m, opL = specOp.size.length_m;
    const midX = (chPos[0] + opPos[0]) / 2;
    const midY = Math.max(chH, opH) / 2;
    const midZ = (chPos[2] + opPos[2]) / 2;
    const halfWidth = Math.abs(chPos[0] - opPos[0]) / 2 + Math.max(chL, opL) / 2;
    const halfHeight = Math.max(chH, opH);
    const radius = Math.sqrt(halfWidth * halfWidth + halfHeight * halfHeight) * pad;
    return { center: [midX, midY, midZ], radius, pad,
             targetY: Math.max(specCh.camera?.target_offset_y ?? 0.7, specOp.camera?.target_offset_y ?? 0.7) };
  }, [chSnap, opSnap, chPos, opPos]);

  const desired = useRef({ pos: [0, 0, 0], look: [0, 0, 0] });
  const currentPos = useRef([0, 3, 8]);
  const currentLook = useRef([0, 1, 0]);
  const shake = useRef({ amp: 0, decay: 0 });
  const prevCursor = useRef(cursor);
  const zoom = useRef(1);
  const targetZoom = useRef(1);

  useFrame((_, dt) => {
    const now = performance.now();

    // -------- Auto-fit baseline (per frame, so it adapts if creatures move) --------
    // Distance needed so the sphere fits ~85% of the shorter viewport axis.
    const aspect = size.width / Math.max(1, size.height);
    const shorterFov = aspect < 1
      ? THREE.MathUtils.degToRad(camera.fov)                            // portrait — height is shorter
      : 2 * Math.atan(Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * aspect); // landscape — width is shorter
    const fitDist = frame.radius / Math.tan(shorterFov / 2) / 0.85;

    // Cinematic angle chosen from event stream
    if (cursor !== prevCursor.current) {
      if (lastLineKind === 'crit') shake.current = { amp: 0.22, decay: 4.5 };
      else if (lastLineKind === 'ultimate') shake.current = { amp: 0.35, decay: 3.2 };
      else if (lastLineKind === 'combo') shake.current = { amp: 0.12, decay: 5.5 };
      else if (lastLineKind === 'dodge') shake.current = { amp: 0.07, decay: 6 };
      targetZoom.current = lastLineKind === 'crit' ? 1.08
                        : lastLineKind === 'ultimate' ? 1.15
                        : lastLineKind === 'combo' ? 1.05
                        : 1;
      prevCursor.current = cursor;
    }
    const angleKey = finished ? 'finale' : intentFor(lastLineKind, cursor);
    const off = ANGLE_OFFSETS[angleKey] || ANGLE_OFFSETS.wide;

    // Convert offset (unit fractions of radius) into absolute world coords
    desired.current.pos = [
      frame.center[0] + off.x * frame.radius * 0.6,
      frame.center[1] + off.y * (frame.radius * 0.35 + 0.4),
      frame.center[2] + off.z * fitDist * 0.35 + fitDist * 0.6,
    ];
    // Look-at target — lifted slightly above the creature's midpoint so
    // the shorter animal doesn't sit near the bottom of a portrait frame.
    // The compensation scales with the viewport aspect so landscape frames
    // don't over-correct upward.
    const portraitBias = aspect < 1 ? 0.35 : 0.15;
    desired.current.look = [
      frame.center[0],
      frame.center[1] + frame.targetY * 0.5 + frame.radius * portraitBias,
      frame.center[2],
    ];

    // Slow-mo scales the lerp speed
    const slowMo = now < slowMoUntil ? 0.35 : 1;
    const lerpK = (lowPower ? 3.5 : 5.0) * dt * slowMo;
    for (let i = 0; i < 3; i++) {
      currentPos.current[i] = THREE.MathUtils.lerp(currentPos.current[i], desired.current.pos[i], lerpK);
      currentLook.current[i] = THREE.MathUtils.lerp(currentLook.current[i], desired.current.look[i], lerpK);
    }

    // Impact shake
    let sx = 0, sy = 0;
    if (shake.current.amp > 0.001) {
      sx = (Math.random() - 0.5) * shake.current.amp;
      sy = (Math.random() - 0.5) * shake.current.amp;
      shake.current.amp = Math.max(0, shake.current.amp - shake.current.decay * dt);
    }

    // Punch-zoom
    zoom.current = THREE.MathUtils.lerp(zoom.current, targetZoom.current, 4 * dt);
    camera.fov = 42 / zoom.current;

    camera.position.set(
      currentPos.current[0] + sx,
      currentPos.current[1] + sy,
      currentPos.current[2],
    );
    camera.lookAt(currentLook.current[0], currentLook.current[1], currentLook.current[2]);
    camera.updateProjectionMatrix();
  });

  return null;
}

function intentFor(kind, cursor) {
  const rotator = ['tracking', 'ots_ch', 'low_wide', 'ots_op'][cursor % 4];
  switch (kind) {
    case 'attack':   return rotator;
    case 'crit':     return 'ots_ch';
    case 'combo':    return 'low_wide';
    case 'dodge':    return 'ots_op';
    case 'ultimate': return 'overhead';
    case 'round_intro': return 'wide';
    case 'round_win':   return 'wide';
    case 'flavor':   return 'wide';
    case 'counter':  return 'ots_op';
    default:         return rotator;
  }
}
